import { Avatar, Box, Flex, Text } from "@chakra-ui/react";
import { CaretDownIcon } from "../../components/icons/CaretDownIcon";
import { useAccountData } from "../../hooks/useAccountData";
import { useFeedStreak } from "./hooks/useFeedStreak";
import { getStreakIntensity } from "./streakIntensity";
import { User } from "../../types";

/**
 * Welcome strip rendered at the top of the suggestion feed before any
 * discovery content. Two variants:
 *   - Authed users get a personal greeting + their streak count, so a
 *     return visit feels like coming home rather than a cold launch.
 *   - Anon users get a brand strip that frames what the feed is for.
 */
export function FeedHeader() {
  const accountData = useAccountData();
  const user = accountData?.user ?? null;

  if (user) {
    return <AuthedHeader user={user} />;
  }
  return <AnonHeader />;
}

function AuthedHeader({ user }: { user: User }) {
  const { streak, bumpStreak } = useFeedStreak();
  const greeting = getTimeGreeting();

  return (
    <Box
      position="relative"
      px={6}
      pt={7}
      pb={4}
      color="white"
      bgGradient="linear(180deg, rgba(255,125,42,0.16) 0%, rgba(196,38,105,0.08) 50%, transparent 100%)"
    >
      <Flex align="flex-start" gap={4}>
        <Avatar
          size="lg"
          name={user.name}
          bg="orange.400"
          color="black"
          boxShadow="0 8px 24px rgba(0,0,0,0.5)"
        />
        <Flex direction="column" flex="1" minW="0" gap={1}>
          <Text fontSize="sm" opacity={0.75} noOfLines={1}>
            {greeting},
          </Text>
          <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1" noOfLines={1}>
            @{user.name}
          </Text>
          <Text fontSize="md" opacity={0.85} noOfLines={2} mt={1}>
            Pick up where you left off
          </Text>
        </Flex>
      </Flex>
      {streak > 0 ? <StreakBadge count={streak} onBump={bumpStreak} /> : null}
      <ScrollHint />
    </Box>
  );
}

/**
 * Branded "scroll for content" affordance — a soft pulsing chevron in the
 * brand orange, anchored just below the header content. Cues the user that
 * the feed begins below.
 */
function ScrollHint() {
  return (
    <Flex justify="center" mt={4} mb={1}>
      <Box
        sx={{
          animation: "feedHeaderBounce 1.8s ease-in-out infinite",
          "@keyframes feedHeaderBounce": {
            "0%, 100%": { transform: "translateY(0)", opacity: 0.5 },
            "50%": { transform: "translateY(4px)", opacity: 1 },
          },
        }}
      >
        <CaretDownIcon boxSize="22px" color="orange.400" />
      </Box>
    </Flex>
  );
}

/**
 * Hero streak badge — bigger as the streak grows. Tier-based intensity
 * drives the glow size, color heat, and tremble amplitude / frequency, so
 * a 100-day streak reads as a vibrating ember and a 1-day streak as a
 * gentle warm chip.
 *
 * For the demo, the badge is a button — clicking it increments the
 * streak so the visual progression can be shown off without waiting days.
 */
function StreakBadge({ count, onBump }: { count: number; onBump: () => void }) {
  const dayWord = count === 1 ? "day" : "days";
  const intensity = getStreakIntensity(count);
  const trembleActive = intensity.trembleAmplitude > 0;
  const amp = intensity.trembleAmplitude;
  const baseShadow = `0 6px ${intensity.badgeGlowPx}px rgba(255, 90, 40, ${intensity.badgeGlowAlpha})`;
  const peakShadow = `0 10px ${intensity.badgeGlowPx + 12}px rgba(255, 90, 40, ${Math.min(1, intensity.badgeGlowAlpha + 0.2)})`;

  return (
    <Flex justify="center" mt={4}>
      <Flex
        as="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onBump();
        }}
        align="center"
        gap={2}
        px={4}
        py={2}
        borderRadius="full"
        bgGradient={`linear(135deg, ${intensity.gradientFrom} 0%, ${intensity.gradientTo} 100%)`}
        color="white"
        fontWeight="bold"
        cursor="pointer"
        sx={{
          // Glow pulse always on; tremble only kicks in past the 7-day tier
          // so lower streaks stay calm. Both run off CSS keyframes defined
          // inline so we can vary amplitude/duration per tier.
          animation: trembleActive
            ? `feedStreakPulse 2.4s ease-in-out infinite, feedStreakShake ${intensity.trembleDuration} linear infinite`
            : "feedStreakPulse 2.4s ease-in-out infinite",
          "@keyframes feedStreakPulse": {
            "0%, 100%": { boxShadow: baseShadow },
            "50%": { boxShadow: peakShadow },
          },
          "@keyframes feedStreakShake": {
            "0%, 100%": { transform: "translate(0, 0)" },
            "20%": { transform: `translate(${-amp}px, ${0.5 * amp}px)` },
            "40%": { transform: `translate(${amp}px, ${-0.5 * amp}px)` },
            "60%": { transform: `translate(${-0.5 * amp}px, ${amp}px)` },
            "80%": { transform: `translate(${0.5 * amp}px, ${-amp}px)` },
          },
        }}
      >
        <Text as="span" fontSize="xl" lineHeight="1">
          🔥
        </Text>
        <Text as="span" fontSize="sm" letterSpacing="0.02em">
          {count}-{dayWord} streak
        </Text>
      </Flex>
    </Flex>
  );
}

function AnonHeader() {
  return (
    <Box
      position="relative"
      px={6}
      pt={7}
      pb={4}
      color="white"
      bgGradient="linear(180deg, rgba(255,125,42,0.16) 0%, rgba(196,38,105,0.08) 50%, transparent 100%)"
    >
      <Flex direction="column" gap={1}>
        <Text
          fontSize="xs"
          fontWeight="extrabold"
          letterSpacing="0.2em"
          textTransform="uppercase"
          color="orange.400"
        >
          Discover
        </Text>
        <Text fontSize="2xl" fontWeight="bold" lineHeight="1.15">
          Find your next favorite comic
        </Text>
        <Text fontSize="md" opacity={0.75} mt={1}>
          Swipe through previews, hop between titles, follow creators you love.
        </Text>
      </Flex>
      <ScrollHint />
    </Box>
  );
}

function getTimeGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
